import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { NzModalService } from 'ng-zorro-antd/modal';

import { NotificationComponent } from '../../../shared/components/notification';

import { throwError as _throw } from 'rxjs';

import { NotificationService } from '../notification';
import { HttpErrorResponse } from '@angular/common/http';

// Define an interface for authentication actions to break the circular dependency
export interface AuthActions {
  logout(): void;
  isExpired(dateExpired: string): boolean;
  expiredDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorsHandlingService {
  // Static property to store auth actions - breaks circular dependency
  private static authActions: AuthActions | null = null;

  constructor(
    private modalService: NzModalService,
    private router: Router,
    private notificationService: NotificationService,
    private notificationComponent: NotificationComponent,
  ) { }

  // Static method to set auth actions
  public static setAuthActions(authActions: AuthActions): void {
    ErrorsHandlingService.authActions = authActions;
  }

  public handleError(error: { code: number, messages: string[] }): boolean {
    if (error.code === 0) {
      this.showNotification('error', 'error', 'error', error.messages.join('\r\n'), null);
      return false;
    }

    if (error.code === 400) {


      error.messages.forEach((message) => {
        this.showNotification('error', 'error', 'error', message, null);
      });

      return false;
    }
    if (error.code === 403) {
      this.showNotification('error', 'error', 'error', error.messages.join('\r\n'), null);
      return false;
    }
    if (error.code === 401) {
      this.handleUnauthorizedError(error);
      return false;
    }

    if (error.code === 404) {
      this.showNotification('error', 'error', 'error', error.messages.join('\r\n'), null);
      if (error.messages.includes('Partner not found')) this.router.navigate(['404']);
      return false;
    }

    if (error.code === 422) {
      const uniqueMessages = [...new Set<string>(error.messages)];
      uniqueMessages.forEach((message) => {
        const width = message.length > 40 ? '450px' : null;
        this.showNotification('error', 'error', 'error', message, width);
      });
      return false;
    }

    if (error.code === 500) {
      this.showNotification('error', 'error', 'error', error.messages.join('\r\n'), null);
      return false;
    }

    return true;
  }

  public showNotification(
    iconType: string,
    color: string,
    notificationType: string,
    description: string,
    width: string | null,
  ): void {
    const data = {
      iconType,
      color,
      notificationType,
      description,
    };

    this.notificationComponent.data = data;
    this.notificationService.showTemplate({ nzData: data, nzStyle: { width: width ?? '385px' } });
  }

  public processError = (error: HttpErrorResponse) => {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      return _throw({ code: 0, messages: ['Application crashed. Please try to refresh the page.'] });
    }
    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong,

    if (error.status === 400) {
      const messages: string[] = error.error?.errors
        ? this.flattenErrorMessages(error.error.errors)
        : [error.error?.message ?? 'Bad request'];
      return _throw({ code: 400, messages });
    }

    if (error.status === 401) {
      if (error.error.hasOwnProperty('has_access_limited')) {
        return _throw({ code: 401, data: error.error });
      } else {
        return _throw({ code: 401, messages: [error.error.error] });
      }
    }

    if (error.status === 403) {
      return _throw({ code: 403, messages: [error.error.message] });
    }

    if (error.status === 404) {
      return _throw({
        code: 404, messages: error.error.error === 'Partner not found'
          ? [error.error.error]
          : ['Can\'t process the request. Requested URL not found.']
      });
    }

    if (error.status === 422) {
      const errors = error.error.errors;
      const messages: string[] = errors ? this.flattenErrorMessages(errors) : [];
      return _throw({ code: 422, messages, errors });
    }

    if (error.status === 500) {
      const messages: string[] = [];
      if (error.error.errors) {
        for (const i in error.error.errors) {
          messages.push(error.error.errors[i]);
        }

        return _throw({ code: 500, messages });
      } else {
        return _throw({ code: 500, messages: ['Server error. Please try again later.'] });
      }
    }

    if (error.status === 503) {
      return _throw({ code: 503, messages: ['Service Unavailable'] });
    }

    return _throw({ code: 0, messages: ['An error occurred. Please try again later.'] });
  };

  private flattenErrorMessages(errors: any): string[] {
    if (typeof errors === 'string') {
      return [errors];
    }
    if (Array.isArray(errors)) {
      return errors.flatMap(item => this.flattenErrorMessages(item));
    }
    if (typeof errors === 'object' && errors !== null) {
      return Object.values(errors).flatMap(value => this.flattenErrorMessages(value));
    }
    return [String(errors)];
  }

  private handleUnauthorizedError(error: any): void {
    // Retrieve the token from localStorage
    const token = localStorage.getItem('token');

    // Generate the appropriate error message
    const errorMessage = this.getErrorMessage(token, error.messages);

    // Show the error notification to the user
    this.showNotification('error', 'error', 'error', errorMessage, null);

    // Close all open modals
    this.modalService.closeAll();

    // Log out the user
    if (ErrorsHandlingService.authActions) {
      ErrorsHandlingService.authActions.logout();
    }

    // Navigate to the login page
    this.router.navigate(['login']);
  }

  private getErrorMessage(token: string | null, messages: string[]): string {
    // Check if the token is expired and return the corresponding message
    if (ErrorsHandlingService.authActions && token) {
      return ErrorsHandlingService.authActions.isExpired(ErrorsHandlingService.authActions.expiredDate)
        ? 'Your authentication token has been expired. Please log in again to continue using the platform'
        : messages.join('\r\n');
    }
    return messages.join('\r\n');
  }
}
